import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { FileUp } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { trpc } from '@/lib/trpc';
import type { Product } from '../../../inventory/types';
import BulkUploadErrorBoundary from '@/components/csv-import-wizard/BulkUploadErrorBoundary';
import StepIndicator from '@/components/csv-import-wizard/StepIndicator';
import UploadStep from './steps/UploadStep';
import ValidationStep from './steps/ValidationStep';
import PreviewStep from './steps/PreviewStep';
import ProcessingStep from './steps/ProcessingStep';
import ResultStep from './steps/ResultStep';
import { usePurchaseOrderCsvUpload } from './usePurchaseOrderCsvUpload';

const PO_CSV_STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'validation', label: 'Validate' },
  { key: 'preview', label: 'Preview' },
  { key: 'processing', label: 'Apply' },
  { key: 'result', label: 'Done' },
];

type ImportItem = {
  productId: string;
  sku: string;
  quantity: number;
  rowCount: number;
  /**
   * Populated when the SKU is new at the current location (no sku_inventory
   * row yet). Parent merges this into its local products list so the row
   * renders in the form. Backend auto-activates the SKU when the order saves.
   */
  newProduct?: Product;
};

interface PurchaseOrderCsvWizardModalProps {
  products: Product[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (items: ImportItem[]) => void;
  /** BLU-373 PR2b: PO currency from parent form, displayed in the header */
  poCurrency?: string | null;
  /** BLU-373 PR2b: Destination location name, displayed in the header */
  locationName?: string | null;
}

const PurchaseOrderCsvWizardModal: React.FC<PurchaseOrderCsvWizardModalProps> = ({
  products,
  isOpen,
  onClose,
  onApply,
  poCurrency,
  locationName,
}) => {
  const store = usePurchaseOrderCsvUpload();

  // Org-wide SKU list — used so the wizard accepts any org SKU even if it's
  // not stocked at the current location (backend auto-activates on save).
  const allSkusQuery = trpc.inventory.listSkus.useQuery({}, { staleTime: 60_000 });
  const allSkus = useMemo(() => allSkusQuery.data ?? [], [allSkusQuery.data]);

  // Reset store on unmount to prevent stale state
  useEffect(() => () => { usePurchaseOrderCsvUpload.getState().reset(); }, []);

  // Also reset when the modal reopens so repeat opens always start fresh.
  useEffect(() => {
    if (isOpen) {
      usePurchaseOrderCsvUpload.getState().reset();
    }
  }, [isOpen]);

  // -----------------------------------------------------------------------
  // Submit handler — synchronous, just calls onApply
  // -----------------------------------------------------------------------
  const handleSubmit = useCallback(() => {
    const { previewRows } = store;

    store.startProcessing(previewRows.length);

    const items: ImportItem[] = previewRows.map((r) => {
      let newProduct: Product | undefined;
      if (r.isNewAtLocation) {
        const orgSku = allSkus.find((s) => s.id === r.productId);
        if (orgSku) {
          const productionType = orgSku.productionType === 'PAP' ? 'PAP' : 'POD';
          // Inherit the SKU's Default cost (sku_cost_override); there is no
          // other org-wide cost. User can still override per-line in the
          // order form.
          newProduct = {
            id: orgSku.id,
            sku: orgSku.sku,
            title: orgSku.title ?? undefined,
            category: {
              type: orgSku.category?.type ?? undefined,
              size: orgSku.category?.size ?? undefined,
              color: orgSku.category?.color ?? undefined,
            },
            stockLevel: 0,
            onHand: 0,
            available: 0,
            committed: 0,
            unavailable: 0,
            incoming: 0,
            image_url: orgSku.imageUrl ?? undefined,
            productionType,
            isLocked: orgSku.isLocked ?? false,
            skuCostOverride: orgSku.skuCostOverride ?? null,
            effectiveCost: orgSku.skuCostOverride ?? undefined,
          };
        }
      }
      return {
        productId: r.productId,
        sku: r.sku,
        quantity: r.quantity,
        rowCount: r.rowCount,
        newProduct,
      };
    });

    try {
      onApply(items);

      store.setResult({
        successCount: items.length,
        failedCount: 0,
        validationErrors: [],
        executionErrors: [],
        mode: 'complete',
      });

      toast({
        title: 'CSV applied',
        description: `Applied ${items.length} SKU${items.length === 1 ? '' : 's'} to your draft order.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      store.setResult({
        successCount: 0,
        failedCount: items.length,
        validationErrors: [],
        executionErrors: [message],
      });

      toast({
        title: 'Failed to apply CSV',
        description: message,
        variant: 'destructive',
      });
    }
  }, [store, onApply, allSkus]);

  // -----------------------------------------------------------------------
  // Close handler
  // -----------------------------------------------------------------------
  const handleClose = useCallback(() => {
    store.reset();
    onClose();
  }, [store, onClose]);

  // -----------------------------------------------------------------------
  // Upload another — reset everything
  // -----------------------------------------------------------------------
  const handleUploadAnother = useCallback(() => {
    store.reset();
  }, [store]);

  // -----------------------------------------------------------------------
  // Go back to validation step (from blocked result)
  // -----------------------------------------------------------------------
  const handleGoBack = useCallback(() => {
    store.clearResult();
    store.setStep('validation');
  }, [store]);

  // -----------------------------------------------------------------------
  // Prevent closing during processing
  // -----------------------------------------------------------------------
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && store.step !== 'processing') {
        handleClose();
      }
    },
    [store.step, handleClose]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <BulkUploadErrorBoundary onReset={() => store.reset()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5 text-threadheads-purple" aria-hidden="true" />
              Bulk Upload Purchase Order Items
            </DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>Upload a CSV of SKUs and order quantities to add to this draft.</span>
              {/* BLU-373 PR2b: surface the PO currency + destination so the user can never miss them.
                  Brand-purple border ties this back to the active toggle pill colour on OrderDetail
                  and the wizard "Apply" CTA so the wizard chrome reads as a single coherent flow. */}
              {(poCurrency || locationName) && (
                <span className="inline-flex items-center gap-2 rounded-md border border-threadheads-purple/40 bg-threadheads-purple/5 px-2 py-0.5 text-xs">
                  {locationName && <span className="font-medium text-threadheads-purple">{locationName}</span>}
                  {locationName && poCurrency && <span className="text-threadheads-purple/50">·</span>}
                  {poCurrency && <span className="font-mono uppercase tracking-wide text-threadheads-purple">{poCurrency}</span>}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicator */}
          <div className="py-2">
            <StepIndicator steps={PO_CSV_STEPS} currentStep={store.step} />
          </div>

          {/* Step content */}
          <div className="min-h-[200px]">
            {store.step === 'upload' && <UploadStep />}

            {store.step === 'validation' && (
              <ValidationStep products={products} allSkus={allSkus} />
            )}

            {store.step === 'preview' && <PreviewStep onSubmit={handleSubmit} />}

            {store.step === 'processing' && <ProcessingStep />}

            {store.step === 'result' && (
              <ResultStep
                onUploadAnother={handleUploadAnother}
                onClose={handleClose}
                onGoBack={handleGoBack}
              />
            )}
          </div>
        </BulkUploadErrorBoundary>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseOrderCsvWizardModal;
